// Read-only, collapsible, syntax-coloured JSON viewer (ADR-131).
//
// Small recursive renderer — no dependency. Every value is rendered as React
// text (escaped), never as HTML: feed content is untrusted and arrives here
// byte-for-byte as the upstream returned it.

import React, { useState } from 'react';
import { Box } from '@mui/material';

const COLORS = {
  key: '#7C3AED',
  string: '#047857',
  number: '#1D4ED8',
  boolean: '#B45309',
  null: '#6B7280',
  punctuation: '#475569',
};

const Primitive = ({ value }) => {
  if (value === null) return <span style={{ color: COLORS.null }}>null</span>;
  if (typeof value === 'string') return <span style={{ color: COLORS.string }}>{JSON.stringify(value)}</span>;
  if (typeof value === 'number') return <span style={{ color: COLORS.number }}>{String(value)}</span>;
  if (typeof value === 'boolean') return <span style={{ color: COLORS.boolean }}>{String(value)}</span>;
  return <span>{String(value)}</span>;
};

const Toggle = ({ open, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-expanded={open}
    aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`}
    style={{
      border: 'none', background: 'none', cursor: 'pointer', padding: 0, width: 14, color: COLORS.punctuation, font: 'inherit',
    }}
  >
    {open ? '▾' : '▸'}
  </button>
);

const Node = ({ name, value, depth, defaultExpandDepth, isLast }) => {
  const isArray = Array.isArray(value);
  const isObject = value !== null && typeof value === 'object';
  const [open, setOpen] = useState(depth < defaultExpandDepth);
  const comma = isLast ? '' : ',';
  const keyPart = name !== undefined ? (
    <>
      <span style={{ color: COLORS.key }}>{JSON.stringify(String(name))}</span>
      <span style={{ color: COLORS.punctuation }}>: </span>
    </>
  ) : null;

  if (!isObject) {
    return (
      <div style={{ paddingLeft: 14 }}>
        {keyPart}<Primitive value={value} /><span style={{ color: COLORS.punctuation }}>{comma}</span>
      </div>
    );
  }

  const entries = isArray ? value.map((v, i) => [i, v]) : Object.entries(value);
  const [openBracket, closeBracket] = isArray ? ['[', ']'] : ['{', '}'];
  const summary = isArray ? `${entries.length} item${entries.length === 1 ? '' : 's'}` : `${entries.length} key${entries.length === 1 ? '' : 's'}`;

  if (entries.length === 0) {
    return (
      <div style={{ paddingLeft: 14 }}>
        {keyPart}<span style={{ color: COLORS.punctuation }}>{openBracket}{closeBracket}{comma}</span>
      </div>
    );
  }

  return (
    <div>
      <div>
        <Toggle open={open} onClick={() => setOpen(!open)} label={name !== undefined ? String(name) : 'root'} />
        {keyPart}
        <span style={{ color: COLORS.punctuation }}>{openBracket}</span>
        {!open && (
          <>
            <span style={{ color: COLORS.null, fontStyle: 'italic' }}>{` ${summary} `}</span>
            <span style={{ color: COLORS.punctuation }}>{closeBracket}{comma}</span>
          </>
        )}
      </div>
      {open && (
        <>
          <div style={{ paddingLeft: 16, borderLeft: '1px dashed #E2E8F0', marginLeft: 6 }}>
            {entries.map(([k, v], i) => (
              <Node
                key={String(k)}
                name={isArray ? undefined : k}
                value={v}
                depth={depth + 1}
                defaultExpandDepth={defaultExpandDepth}
                isLast={i === entries.length - 1}
              />
            ))}
          </div>
          <div style={{ paddingLeft: 14, color: COLORS.punctuation }}>{closeBracket}{comma}</div>
        </>
      )}
    </div>
  );
};

/**
 * @param {Object} props
 * @param {*} props.value - any JSON-serialisable value
 * @param {number} [props.defaultExpandDepth=2]
 */
const JsonViewer = ({ value, defaultExpandDepth = 2 }) => (
  <Box
    component="pre"
    sx={{
      m: 0, p: 2, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12.5, lineHeight: 1.6,
      backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 1, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }}
  >
    <Node value={value} depth={0} defaultExpandDepth={defaultExpandDepth} isLast />
  </Box>
);

export default JsonViewer;
